import React from 'react';
import { ShieldCheck, Lock, Activity, Cloud, MapPin, Shield } from 'lucide-react';
import { motion } from 'framer-motion';

const badges = [
  { label: 'End-to-End Encryption', Icon: Lock },
  { label: '99.99% Uptime SLA', Icon: Activity },
  { label: 'Hosted on AWS', Icon: Cloud },
  { label: 'Data Stored in Canada', Icon: MapPin },
  { label: 'GDPR-Ready', Icon: Shield },
];

const pillars = [
  {
    title: 'Secure by Design',
    description:
      'Security is built into every layer of Ufficient, from infrastructure to application design. All data is encrypted in transit (TLS 1.2+) and at rest (AES-256). Access controls leverage least-privilege IAM roles and SSO/OIDC where available.',
  },
  {
    title: 'Transparent Compliance',
    description:
      'We follow industry best practices such as SOC 2 Type II and GDPR. Independent auditors assess our controls and publish reports you can review under NDA.',
  },
  {
    title: 'High Availability',
    description:
      'Our multi-AZ architecture and automated fail-over deliver a 99.99 % uptime SLA. Continuous monitoring and on-call engineering ensure rapid incident response.',
  },
  {
    title: 'Data Residency',
    description:
      'All customer data is stored exclusively in Canadian AWS regions (ca-central-1). Back-ups never leave Canada.',
  },
];

const highlights = [
  "Every organization's data is isolated in its own dedicated S3 bucket and private vector store—never co-mingled.",
  "We leverage OpenAI's world-class SOC 2 & SOC 3–certified APIs that are trusted by thousands of enterprises.",
  'Adding a document to Ufficient is as secure as sending an email—TLS in transit, encrypted at rest, and strict access controls.',
];

const TrustPage = () => {
  return (
    <div className="pb-16 bg-white">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-indigo-100 py-32 md:py-40">
        {/* animated background blobs */}
        <motion.div
          className="absolute -top-32 -left-32 w-96 h-96 bg-blue-300 rounded-full filter blur-3xl opacity-20"
          animate={{ x: [0, 30, -20, 0], y: [0, 40, -30, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute -bottom-32 -right-32 w-96 h-96 bg-indigo-300 rounded-full filter blur-3xl opacity-20"
          animate={{ x: [0, -30, 20, 0], y: [0, -40, 30, 0] }}
          transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
        />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-gray-900 mb-6"
          >
            Trust &amp; Security
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-lg sm:text-xl text-gray-700 max-w-2xl mx-auto"
          >
            Your data's safety is our top priority. Learn how Ufficient protects your information and keeps your team compliant.
          </motion.p>
          <div className="mt-10 flex flex-wrap justify-center items-center gap-4 sm:gap-6">
            {badges.map(({ label, Icon }) => (
              <span
                key={label}
                className="inline-flex items-center px-4 py-2 bg-white border border-gray-200 rounded-full shadow-sm text-gray-700 text-sm sm:text-base font-medium whitespace-nowrap"
              >
                <Icon size={18} className="text-blue-600 mr-2" />
                {label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Architecture Diagram */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-28">

        <div className="md:flex md:items-center md:gap-14">
          {/* Text */}
          <motion.div
            className="md:w-1/2 text-gray-800 leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-3xl font-semibold text-center md:text-left mb-8 text-gray-900 mt-12 md:mt-20"
        >
          How Your Data Flows Through Ufficient
        </motion.h2>
            <p className="mb-6">
              All data transfers to and from Ufficient are protected using TLS&nbsp;1.2+ encryption in transit. Once ingested, documents are stored in your organization's own dedicated S3 bucket and a private vector database located inside your AWS VPC—no data ever leaves your tenancy.
            </p>
            <p>
              When your team performs a search or asks a question, our servers generate embeddings and, if needed, call OpenAI's SOC&nbsp;2/SOC&nbsp;3–certified APIs. Only the minimal prompt context is transmitted, and it is never used to train foundation models.
            </p>
          </motion.div>

          {/* Diagram */}
          <motion.img
            src="/Ufficient%20High%20Level%20Architecture.png"
            alt="Ufficient architecture diagram"
            className="md:w-1/2 mx-auto rounded-lg shadow-lg border border-gray-100 mt-10 md:mt-0"
            loading="lazy"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          />
        </div>
      </section>

      {/* Pillars – horizontal cards */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-24">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-3">
          {pillars.map((pillar, idx) => (
            <motion.div
              key={pillar.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              viewport={{ once: true }}
              className="bg-white shadow-lg rounded-2xl p-8 relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-blue-600 opacity-5 rounded-2xl"></div>
              <h2 className="text-xl font-semibold mb-4 relative z-10 text-gray-900">
                {pillar.title}
              </h2>
              <p className="text-gray-800 leading-relaxed relative z-10">
                {pillar.description}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Additional Highlights */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-24">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-3xl font-semibold text-center mb-10 text-gray-900"
        >
          Additional Safeguards
        </motion.h2>
        <ul className="space-y-6 text-gray-700 text-lg list-disc list-inside">
          {highlights.map((item, i) => (
            <motion.li
              key={i}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
            >
              {item}
            </motion.li>
          ))}
        </ul>
      </section>
    </div>
  );
};

export default TrustPage; 